import { type Ref, isRef, ref } from "@nuxtjs/composition-api";
import { type AxiosError } from "axios";
import { defineStore } from "pinia";

import useCreatorProductApi from "@/store/creator-products/-api";
import {
  type CreatorProductActivityServiceMeta,
  type CreatorProductComposedProduct,
  type CreatorProductDetailMap,
  type CreatorProductMap,
  type CreatorProductMediaObjectMap,
  type CreatorProductSearchServiceMeta,
  type CreatorProductStore,
  type CreatorRecentProductSearchFeed,
  type CreatorRecentProductsFeed,
  type CreatorTrendingProductsFeed,
} from "@/store/creator-products/types";
import { DEFAULT_API_RESPONSE_LIMIT, DEFAULT_FETCH_STATE, DEFAULT_SEARCH_SERVICE_META } from "@/utils/constants";
import logger, { logLevel } from "@/utils/logger";
import mapData from "@/utils/transform/map-data";

/**
 * TODO - refactor this once we move to `/v3/posts` as the models will be in alignment.
 * For now we temporarily cache the data in this store to avoid conflicts with older models
 */
export const useCreatorProductStore = defineStore(
  "creator-products",
  () => {
    // Models
    const products = ref<CreatorProductMap>({});
    const productDetails = ref<CreatorProductDetailMap>({});
    const mediaObjects = ref<CreatorProductMediaObjectMap>({});
    // Feeds
    const recentProductsFeed = ref<CreatorRecentProductsFeed>({});
    const trendingProductsFeed = ref<CreatorTrendingProductsFeed>({});
    const recentProductsSearchFeed = ref<CreatorRecentProductSearchFeed>({});
    // FetchState
    const recentProductsFetchState = ref<FetchState>(DEFAULT_FETCH_STATE);
    const trendingProductsFetchState = ref<FetchState>(DEFAULT_FETCH_STATE);
    const recentProductsSearchFetchState = ref<FetchState>(DEFAULT_FETCH_STATE);

    const hasRecentProductsBeenFetched = (profileId: string): boolean =>
      Boolean(recentProductsFeed.value[profileId]?.ids?.length);

    const hasTrendingProductsBeenFetched = (profileId: string): boolean =>
      Boolean(trendingProductsFeed.value[profileId]?.ids?.length);

    const doesRecentProductsHaveMorePages = (profileId: string): boolean =>
      Boolean(recentProductsFeed.value[profileId]?.meta?.next);

    const doseRecentProductsSearchHaveMorePages = (search: string): boolean => {
      const feed = recentProductsSearchFeed.value[search] ?? { ids: [], meta: DEFAULT_SEARCH_SERVICE_META };
      const currentPage = feed?.meta?.page; // zero-based index paging
      const lastPage = Math.abs(feed?.meta?.nbPages - 1);
      return Boolean(currentPage < lastPage);
    };

    // Note this is not reactive, and while Pinia supports getters with arguments. It's not best practice, and doesn't actually cache values.
    // https://pinia.vuejs.org/core-concepts/getters.html#Passing-arguments-to-getters
    const getProductById = (productId: string): CreatorProductComposedProduct | null => {
      const product = products.value[productId];
      const mediaObjectId = product?.mediaObjectIds?.[0];
      const mediaObject = mediaObjectId ? mediaObjects.value[mediaObjectId] : null;
      const productDetail = productDetails.value[product?.productReferenceId];

      return product && mediaObject && productDetail ? { ...product, mediaObject, productDetail } : null;
    };

    const setFetchState = ({
      ref,
      pending = false,
      error = null,
      timestamp,
    }: Partial<FetchState> & { ref: Ref<FetchState> }): void => {
      isRef(ref) && (ref.value = { pending, error, timestamp: timestamp ?? Date.now() });
    };

    const setProducts = (data: CreatorProductMap): void => {
      products.value = { ...products.value, ...data };
    };

    const setProductDetails = (data: CreatorProductDetailMap): void => {
      productDetails.value = { ...productDetails.value, ...data };
    };

    const setMediaObjects = (data: CreatorProductMediaObjectMap): void => {
      mediaObjects.value = { ...mediaObjects.value, ...data };
    };

    const setTrendingProductsFeed = (profileId: string, ids: Array<string>): void => {
      trendingProductsFeed.value[profileId] = { ids };
    };

    const setRecentProductsFeed = (
      profileId: string,
      ids: Array<string>,
      meta: CreatorProductActivityServiceMeta,
    ): void => {
      if (recentProductsFeed.value[profileId]) {
        const existingIds = recentProductsFeed.value[profileId]?.ids ?? [];
        recentProductsFeed.value[profileId].ids = [...existingIds, ...ids];
        recentProductsFeed.value[profileId].meta = meta;
      } else {
        recentProductsFeed.value[profileId] = { ids, meta };
      }
    };
    const setRecentProductsSearchFeed = (
      search: string,
      ids: Array<string>,
      meta: CreatorProductSearchServiceMeta,
    ): void => {
      if (recentProductsSearchFeed.value[search]) {
        const existingIds = recentProductsSearchFeed.value[search]?.ids ?? [];
        recentProductsSearchFeed.value[search].ids = [...existingIds, ...ids];
        recentProductsSearchFeed.value[search].meta = meta;
      } else {
        recentProductsSearchFeed.value[search] = { ids, meta };
      }
    };

    const clearRecentProductsSearchFeed = () => {
      recentProductsSearchFeed.value = {};
    };

    const fetchCreatorRecentProducts: CreatorProductStore["fetchCreatorRecentProducts"] = async function (
      profileId: string,
    ) {
      if (
        !profileId ||
        recentProductsFetchState.value.pending === true ||
        (hasRecentProductsBeenFetched(profileId) && !doesRecentProductsHaveMorePages(profileId))
      ) {
        return;
      }

      try {
        setFetchState({ ref: recentProductsFetchState, pending: true });

        const feedMeta = recentProductsFeed.value[profileId]?.meta ?? {
          next: "",
          limit: DEFAULT_API_RESPONSE_LIMIT,
        };

        const response = await this.$api.getCreatorRecentProducts({ profileId, next: feedMeta.next });

        if (response !== null) {
          const {
            products: responseProducts,
            mediaObjects: responseMedia,
            productDetails: responseDetails,
            meta: responseMeta,
          } = response?.data ?? {};

          if (responseProducts && responseMedia && responseDetails && responseMeta) {
            const productIds = responseProducts.map((product) => product.productReferenceId) ?? [];
            setProducts(mapData("productReferenceId", responseProducts)); // We key off the product_reference_id as that's the key other models/side-loads use
            setProductDetails(mapData("id", responseDetails)); // We use the id here which matches the product_reference_id
            setMediaObjects(mapData("id", responseMedia)); // We can use id here as a product contains an array of media object id's
            setRecentProductsFeed(profileId, productIds, responseMeta);
          }
        } else {
          setRecentProductsFeed(profileId, [], { next: "", limit: DEFAULT_API_RESPONSE_LIMIT });
        }

        setFetchState({ ref: recentProductsFetchState, pending: false });
      } catch (error) {
        logger(logLevel.ERROR, `Error during fetchCreatorRecentProducts()`, {}, { meta: { error, profileId } });
        setFetchState({ ref: recentProductsFetchState, pending: false, error: error as AxiosError });
      }
    };

    const fetchCreatorTrendingProducts: CreatorProductStore["fetchCreatorTrendingProducts"] = async function (
      profileId: string,
    ) {
      if (
        !profileId ||
        trendingProductsFetchState.value.pending === true ||
        hasTrendingProductsBeenFetched(profileId)
      ) {
        return;
      }

      try {
        setFetchState({ ref: trendingProductsFetchState, pending: true });

        const response = await this.$api.getCreatorTrendingProducts({ profileId });

        if (response !== null) {
          const {
            products: responseProducts,
            mediaObjects: responseMedia,
            productDetails: responseDetails,
          } = response?.data ?? {};

          if (responseProducts && responseMedia && responseDetails) {
            const productIds = responseProducts.map((product) => product.productReferenceId) ?? [];
            setProducts(mapData("productReferenceId", responseProducts)); // We key off the product_reference_id as that's the key other models/side-loads use
            setProductDetails(mapData("id", responseDetails)); // We use the id here which matches the product_reference_id
            setMediaObjects(mapData("id", responseMedia)); // We can use id here as a product contains an array of media object id's
            setTrendingProductsFeed(profileId, productIds);
          }
        } else {
          setTrendingProductsFeed(profileId, []);
        }

        setFetchState({ ref: trendingProductsFetchState, pending: false });
      } catch (error) {
        logger(logLevel.ERROR, `Error during fetchCreatorTrendingProducts()`, {}, { meta: { error, profileId } });
        setFetchState({ ref: trendingProductsFetchState, pending: false, error: error as AxiosError });
      }
    };

    const searchCreatorRecentProducts: CreatorProductStore["searchCreatorRecentProducts"] = async function (
      profileId: string,
      search: string,
    ) {
      const feed = recentProductsSearchFeed.value[search];
      const hasNoMorePages = !doseRecentProductsSearchHaveMorePages(search);
      const isFetching = recentProductsSearchFetchState.value.pending === true;

      if (!profileId || !search || (feed && hasNoMorePages) || isFetching) {
        return;
      }

      try {
        setFetchState({ ref: recentProductsSearchFetchState, pending: true });

        const response = await this.$api.searchCreatorRecentProducts({
          profileId,
          query: search,
          page: feed ? feed?.meta?.page + 1 : 0,
        });

        if (response !== null) {
          const {
            products: responseProducts,
            mediaObjects: responseMedia,
            productDetails: responseDetails,
            meta: responseMeta,
          } = response?.data ?? {};

          if (responseProducts && responseMedia && responseDetails && responseMeta) {
            if (!feed) {
              recentProductsSearchFeed.value = {}; // New search, so clear feed
            }

            const productIds = responseProducts.map((product) => product.productReferenceId) ?? [];
            setProducts(mapData("productReferenceId", responseProducts)); // We key off the product_reference_id as that's the key other models/side-loads use
            setProductDetails(mapData("id", responseDetails)); // We use the id here which matches the product_reference_id
            setMediaObjects(mapData("id", responseMedia)); // We can use id here as a product contains an array of media object id's// We key off the product_reference_id as that's the key other models/side-loads use
            setRecentProductsSearchFeed(search, productIds, responseMeta);
          }
        } else {
          setRecentProductsSearchFeed(search, [], DEFAULT_SEARCH_SERVICE_META);
        }

        setFetchState({ ref: recentProductsSearchFetchState, pending: false });
      } catch (error) {
        logger(
          logLevel.ERROR,
          `Error during searchCreatorRecentProducts()`,
          {},
          { meta: { error, profileId, search } },
        );
        setFetchState({ ref: recentProductsSearchFetchState, pending: false, error: error as AxiosError });
        recentProductsSearchFeed.value = {}; // clear feed just in case
      }
    };

    const $reset = () => {
      products.value = {};
      mediaObjects.value = {};
      productDetails.value = {};
      trendingProductsFetchState.value = DEFAULT_FETCH_STATE;
      recentProductsFetchState.value = DEFAULT_FETCH_STATE;
      recentProductsSearchFetchState.value = DEFAULT_FETCH_STATE;
      trendingProductsFeed.value = {};
      recentProductsFeed.value = {};
      recentProductsSearchFeed.value = {};
    };

    return {
      products,
      mediaObjects,
      productDetails,
      recentProductsFetchState,
      trendingProductsFetchState,
      recentProductsSearchFetchState,
      trendingProductsFeed,
      recentProductsFeed,
      recentProductsSearchFeed,
      setFetchState,
      setProducts,
      setProductDetails,
      setMediaObjects,
      setRecentProductsFeed,
      setTrendingProductsFeed,
      setRecentProductsSearchFeed,
      clearRecentProductsSearchFeed,
      hasRecentProductsBeenFetched,
      hasTrendingProductsBeenFetched,
      doesRecentProductsHaveMorePages,
      doseRecentProductsSearchHaveMorePages,
      getProductById, // this is not reactive
      fetchCreatorRecentProducts,
      fetchCreatorTrendingProducts,
      searchCreatorRecentProducts,
      $reset,
    };
  },
  {
    api: useCreatorProductApi,
  },
);
